<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click.self="close"
  >
    <div class="bg-white rounded-2xl shadow-2xl w-[90%] max-w-lg p-6">
      <h3 class="text-lg font-bold text-gray-800 mb-1">Вернуть изделие на маршрут</h3>
      <p class="text-sm text-gray-500 mb-4">Изделие {{ productLabel }}</p>

      <!-- Загрузка условий возврата -->
      <div v-if="loading" class="text-sm text-gray-500 py-4">Проверяем условия возврата…</div>

      <!-- Итог выполненного возврата: сводку показываем как есть, её собрал бэкенд -->
      <template v-else-if="result">
        <p class="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4">
          {{ result.summary || 'Изделие возвращено на маршрут' }}
        </p>
        <div class="flex justify-end">
          <button
            type="button"
            class="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
            @click="close"
          >
            Закрыть
          </button>
        </div>
      </template>

      <template v-else>
        <!-- Документ качества, открывший возврат -->
        <div v-if="document" class="mb-4 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <div class="font-medium">{{ document.title }}</div>
          <div v-if="document.product_action_label" class="text-gray-500 mt-0.5">
            Решение по продукту: {{ document.product_action_label }}
          </div>
        </div>

        <!-- Возврат закрыт: причина приходит готовым текстом -->
        <template v-if="options && !options.is_available">
          <p class="text-sm text-gray-700 mb-5 whitespace-pre-line break-words">
            {{ options.reason || 'Возврат на маршрут сейчас недоступен.' }}
          </p>
          <div class="flex justify-end">
            <button
              type="button"
              class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              @click="close"
            >
              Закрыть
            </button>
          </div>
        </template>

        <template v-else-if="options">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Операция, с которой продолжится маршрут
            <span v-if="options.operation_required" class="text-red-600">*</span>
          </label>
          <div class="v-select-filter" style="width: 100%; flex: none;">
            <v-select
              v-model="selectedOperationId"
              :options="operations"
              :reduce="operation => operation.id"
              :get-option-label="operationLabel"
              :clearable="false"
              :disabled="submitting"
              placeholder="Выберите операцию"
            >
              <template #option="operation">
                <div class="py-0.5">
                  <div class="font-medium text-gray-800">{{ operationName(operation) }}</div>
                  <div class="text-xs text-gray-500">
                    {{ operation.workshop_post_name || 'Рабочий центр не указан' }} ·
                    {{ operation.status_label }}
                    <span v-if="operation.attempts_count > 0"> · {{ attemptsLabel(operation.attempts_count) }}</span>
                  </div>
                </div>
              </template>
            </v-select>
          </div>

          <p v-if="options.operation_required" class="text-xs text-gray-500 mt-2">
            Операция, на которой зафиксировано несоответствие, в случае не указана — выберите её вручную.
          </p>
          <p v-if="selectedOperationAttempts > 0" class="text-xs text-amber-700 mt-2">
            Эту операцию уже проводили: {{ attemptsLabel(selectedOperationAttempts) }}. Прошлые попытки сохранятся в истории.
          </p>
          <p class="text-xs text-gray-500 mt-2">
            Выбранная операция и следующие за ней проводятся заново, предыдущие сохраняют зачёт.
            Серийный номер изделия не меняется.
          </p>

          <p v-if="error" class="text-sm text-red-600 mt-3 whitespace-pre-line break-words">{{ error }}</p>

          <div class="flex justify-end gap-2 mt-5">
            <button
              type="button"
              class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
              :disabled="submitting"
              @click="close"
            >
              Отмена
            </button>
            <button
              type="button"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              :disabled="submitting"
              @click="submit"
            >
              {{ submitting ? 'Возвращаем…' : 'Вернуть на маршрут' }}
            </button>
          </div>
        </template>

        <!-- Условия возврата получить не удалось -->
        <template v-else>
          <p class="text-sm text-red-600 mb-5 whitespace-pre-line break-words">
            {{ error || 'Не удалось получить условия возврата на маршрут.' }}
          </p>
          <div class="flex justify-end">
            <button
              type="button"
              class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              @click="close"
            >
              Закрыть
            </button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

/**
 * Штатный возврат изделия на маршрут по согласованному документу качества.
 *
 * Отдельное действие от ручного «Вернуть в работу»: ручное снимает ошибочную остановку
 * без документов, а это идёт по решению службы качества и позволяет выбрать операцию,
 * с которой продолжается маршрут.
 *
 * Открыт ли возврат и чем его наполнить, решает бэкенд (getReturnOptions): причина
 * отказа и подписи операций приходят готовым текстом, компонент их только показывает.
 */
export default {
  name: 'RouteReturnModal',
  props: {
    show: { type: Boolean, default: false },
    // Изделие и/или случай несоответствия — бэкенду достаточно любого из двух
    batchProductId: { type: [Number, String], default: null },
    nonconformityCaseId: { type: [Number, String], default: null },
    // Условия возврата, уже загруженные родителем: избавляет от повторного запроса
    initialOptions: { type: Object, default: null },
  },
  emits: ['close', 'returned'],
  data() {
    return {
      options: null,
      loading: false,
      submitting: false,
      selectedOperationId: null,
      error: '',
      result: null,
    };
  },
  computed: {
    productLabel() {
      const serialNumber = this.options?.serial_number;
      return serialNumber ? `№ ${serialNumber}` : 'без серийного номера';
    },
    document() {
      return this.options?.document || null;
    },
    // Порядок операций по маршруту: показываем так, как их проходит изделие
    operations() {
      const list = this.options?.operations || [];
      return [...list].sort((a, b) => (a.sort || 0) - (b.sort || 0));
    },
    selectedOperation() {
      return this.operations.find(operation => operation.id === this.selectedOperationId) || null;
    },
    selectedOperationAttempts() {
      return this.selectedOperation?.attempts_count || 0;
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.init();
      } else {
        this.reset();
      }
    },
  },
  mounted() {
    if (this.show) this.init();
  },
  methods: {
    init() {
      this.reset();
      if (this.initialOptions) {
        this.applyOptions(this.initialOptions);
        return;
      }
      this.loadOptions();
    },
    reset() {
      this.options = null;
      this.selectedOperationId = null;
      this.error = '';
      this.result = null;
      this.submitting = false;
      this.loading = false;
    },
    applyOptions(options) {
      this.options = options || null;
      // После отклонения акта выпуска подставлять нечего — сотрудник выбирает операцию сам
      this.selectedOperationId = this.options?.operation_required
        ? null
        : (this.options?.default_operation_id ?? null);
    },
    // Изделие адресуется случаем или самим изделием: берём то, что известно
    identityPayload() {
      const payload = {};
      const caseId = this.options?.nonconformity_case_id ?? this.nonconformityCaseId;
      const batchProductId = this.options?.batch_product_id ?? this.batchProductId;
      if (caseId) payload.nonconformity_case_id = Number(caseId);
      if (batchProductId) payload.batch_product_id = Number(batchProductId);
      return payload;
    },
    async loadOptions() {
      const payload = this.identityPayload();
      if (!payload.nonconformity_case_id && !payload.batch_product_id) {
        this.error = 'Не указано, по какому изделию нужны условия возврата.';
        return;
      }

      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/nonconformity/getReturnOptions', payload);
        if (data?.success) {
          this.applyOptions(data.data);
          return;
        }
        this.error = data?.message || 'Не удалось получить условия возврата на маршрут.';
      } catch (error) {
        this.error = this.extractError(error, 'Не удалось получить условия возврата на маршрут.');
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (this.submitting) return;

      // Те же правила, что в сервисе возврата: без операции маршрут продолжить не с чего
      if (!this.selectedOperationId) {
        this.error = 'Выберите операцию, с которой продолжается маршрут: операция, на которой '
          + 'зафиксировано несоответствие, в случае не указана.';
        return;
      }

      this.submitting = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/nonconformity/returnToRoute', {
          ...this.identityPayload(),
          production_task_operation_id: this.selectedOperationId,
        });
        if (data?.success) {
          this.result = data.data || {};
          this.$emit('returned', this.result);
          return;
        }
        this.error = data?.message || 'Не удалось вернуть изделие на маршрут.';
      } catch (error) {
        this.error = this.extractError(error, 'Не удалось вернуть изделие на маршрут.');
      } finally {
        this.submitting = false;
      }
    },
    close() {
      if (this.submitting) return;
      this.$emit('close');
    },
    operationName(operation) {
      return operation?.name || 'Операция без названия';
    },
    // Подпись в закрытом селекте: сырые ключи статуса на экран не выводим
    operationLabel(operation) {
      const parts = [this.operationName(operation)];
      if (operation?.workshop_post_name) parts.push(operation.workshop_post_name);
      if (operation?.status_label) parts.push(operation.status_label);
      return parts.join(' · ');
    },
    attemptsLabel(count) {
      const number = Number(count) || 0;
      const lastTwo = number % 100;
      const last = number % 10;
      let word = 'раз';
      if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) word = 'раза';
      return `${number} ${word}`;
    },
    // Ошибки валидации (422) приходят готовым текстом — показываем их пользователю
    extractError(error, fallback) {
      const payload = error?.response?.data;
      const details = payload?.errors && typeof payload.errors === 'object'
        ? Object.values(payload.errors).flat().filter(Boolean)
        : [];
      return details.length ? details.join('\n') : (payload?.message || fallback);
    },
  },
};
</script>
